import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import theme from 'styled-theming';
import CardSde from '../components/SdeComponents/CardSde';
import {
  Heading, Link, Loading, Section, Stepper, Subtitle, Toggle, FeatureGate,
} from '../components';
import api from '../api';
import SdeSectionList from '../components/SdeComponents/SdeSectionList';
import { transformSdeSection } from '../components/SdeComponents/SdeDataTransformer';
import { cardData } from '../components/SdeComponents/CardData';
import { useAppState, useNotification } from '../hooks';

const LINK_TO_DOCS = 'https://developers.liveperson.com/engagement-attributes-api-engagement-attributes.html';
const LINK_TO_SDE = 'https://developers.liveperson.com/api-guidelines-engagement-attributes.html';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const ToggleLabel = styled(Text)`
  color: ${textColor};
`;

function SdePage(props) {
  const {
    name, subtitle,
  } = props;

  const isMounted = useRef(false);
  const [authSdeEnabled, setAuthSdeEnabled] = useState(false);
  const [unauthSdeEnabled, setUnauthSdeEnabled] = useState(false);
  const [isLoadingAuthSdeEnabled, setLoadingAuthSdeEnabled] = useState(true);
  const [isLoadingUnauthSdeEnabled, setLoadingUnauthSdeEnabled] = useState(true);
  const [resultList, setResultList] = useState([]);
  const [sectionDataArray, setSectionDataArray] = useState([]);
  const [selectedCardIdx, setSelectedCardIdx] = useState(0);
  const [selectedCard, setSelectedCard] = useState(null);
  const {
    isCcsV2, isLoading: isAppStateLoading, themeMode, accountId: brandId,
  } = useAppState();
  const { addNotification } = useNotification();

  const fetchSdeAttributes = useCallback(async (accId, isV2) => {
    try {
      const result = isV2
        ? await api.ccsV2.getSdeList(accId)
        : await api.mavenContext.getSdeList(accId);

      setResultList(result);
    } catch (error) {
      setResultList([]);
      addNotification(error, 'error');
    }
  }, [addNotification]);

  const fetchAuthSdeToggleState = useCallback(
    async (accId) => {
      try {
        if (isMounted.current) {
          setLoadingAuthSdeEnabled(true);
        }

        const isAuthSdeFlagEnabled = await api.mavenConfig.getAuthSdeStatus(accId);
        if (isMounted.current) {
          setAuthSdeEnabled(isAuthSdeFlagEnabled);
        }
      } finally {
        if (isMounted.current) {
          setLoadingAuthSdeEnabled(false);
        }
      }
    },
    [],
  );

  const fetchUnauthSdeToggleState = useCallback(
    async (accId) => {
      try {
        if (isMounted.current) {
          setLoadingUnauthSdeEnabled(true);
        }

        const isunAuthSdeFlagEnabled = await api.launchDarkly.getSdeStatus(accId);
        if (isMounted.current) {
          setUnauthSdeEnabled(isunAuthSdeFlagEnabled);
        }
      } finally {
        if (isMounted.current) {
          setLoadingUnauthSdeEnabled(false);
        }
      }
    }, [],
  );

  const handleAuthSdeToggleButtonPressed = useCallback(
    async (e) => {
      const desiredState = e.target.checked;
      try {
        setLoadingAuthSdeEnabled(true);
        await api.mavenConfig.updateAuthSdeStatus(brandId, desiredState);
        fetchAuthSdeToggleState(brandId);
      } catch (error) {
        setLoadingAuthSdeEnabled(false);
        addNotification(error, 'error');
      }
    },
    [addNotification, brandId, fetchAuthSdeToggleState],
  );

  const handleUnauthSdeToggleButtonPressed = useCallback(
    async (e) => {
      const desiredState = e.target.checked;
      try {
        setLoadingUnauthSdeEnabled(true);
        await api.launchDarkly.setSdeStatus(brandId, desiredState);
        fetchUnauthSdeToggleState(brandId);
      } catch (error) {
        setLoadingUnauthSdeEnabled(false);
        addNotification(error, 'error');
      }
    },
    [addNotification, brandId, fetchUnauthSdeToggleState],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAppStateLoading) {
      fetchSdeAttributes(brandId, isCcsV2);
      fetchAuthSdeToggleState(brandId);
      fetchUnauthSdeToggleState(brandId);
    }
  }, [brandId, fetchSdeAttributes, isCcsV2, isAppStateLoading,
    fetchAuthSdeToggleState, fetchUnauthSdeToggleState]);

  useEffect(() => {
    if (isMounted.current) {
      setSelectedCard(cardData[selectedCardIdx]);
      if (resultList.length) {
        setSectionDataArray(transformSdeSection(cardData[selectedCardIdx].cardTitle, resultList));
      }
    }
  }, [resultList, selectedCardIdx]);

  async function handleCardClick(idx) {
    if (isMounted.current) {
      setSelectedCardIdx(idx);
    }
  }

  return (
    <Box pb={4}>
      <Heading>{name}</Heading>
      <Subtitle mb={3}>{subtitle}</Subtitle>
      <Section
        section="custom-getting-started"
        title="Getting started"
        subtitle={(
          <Box>
            To learn how to use SDEs in Conversation Context Service please see
            {' '}
            <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
              documentation
            </Link>
            .
          </Box>
        )}
      >
        <Stepper
          steps={[
            {
              id: 1,
              title: (
                <Box>
                  Set up and manage SDE attributes in
                  {' '}
                  <Link href={LINK_TO_SDE} target="_blank" rel="noopener noreferrer">Conversation Cloud</Link>
                </Box>
              ),
            },
            {
              id: 2,
              title: 'Enable Conversation Context Service to store SDE values',
            },
            { id: 3, title: 'Start using policies' },
          ]}
          activeStep={2}
        />
      </Section>
      <Section
        title="Enable Conversation Context Service to store SDE values"
        subtitle="Enabling this will allow Conversation Context Service to get access and store SDE data."
      >
        {/* AUTH SDEs */}

        <Flex alignItems="center">
          <ToggleLabel
            ml={2}
            fontSize={1}
            fontWeight={300}
            color={authSdeEnabled ? 'success' : 'midGrey'}
            onClick={(event) => event.stopPropagation()}
            data-testid="auth-sde-toggle-label"
            width="150px"
          >
            {`${'Authenticated SDE: '}`}
          </ToggleLabel>
          {isLoadingAuthSdeEnabled ? <Loading size={24} /> : (
            <>
              <Toggle
                className={themeMode === 'light' ? null : 'dark-theme'}
                icons={false}
                checked={authSdeEnabled === true}
                onChange={handleAuthSdeToggleButtonPressed}
                data-testid="auth-sde-toggle"
              />
              <ToggleLabel
                ml={2}
                fontSize={1}
                fontWeight={300}
                color={authSdeEnabled ? 'success' : 'midGrey'}
                onClick={(event) => event.stopPropagation()}
                data-testid="auth-sde-toggle-label"
              >
                {`${authSdeEnabled ? 'enabled' : 'disabled'}`}
              </ToggleLabel>
            </>
          )}

        </Flex>

        {/* UNAUTH SDEs */}
        <FeatureGate feature="maven.feature.showUnauthSdeToggle">
          <Flex alignItems="center" marginTop="10px">
            <ToggleLabel
              ml={2}
              fontSize={1}
              fontWeight={300}
              color={unauthSdeEnabled ? 'success' : 'midGrey'}
              onClick={(event) => event.stopPropagation()}
              data-testid="unauth-sde-toggle-label"
              width="150px"
            >
              {`${'Unauthenticated SDE: '}`}
            </ToggleLabel>
            {isLoadingUnauthSdeEnabled ? <Loading size={24} /> : (
              <>
                <Toggle
                  className={themeMode === 'light' ? null : 'dark-theme'}
                  icons={false}
                  checked={unauthSdeEnabled}
                  onChange={handleUnauthSdeToggleButtonPressed}
                  data-testid="unauth-sde-toggle"
                />
                <ToggleLabel
                  ml={2}
                  fontSize={1}
                  fontWeight={300}
                  color={unauthSdeEnabled ? 'success' : 'midGrey'}
                  onClick={(event) => event.stopPropagation()}
                  data-testid="unauth-sde-toggle-label"
                >
                  {`${unauthSdeEnabled ? 'enabled' : 'disabled'}`}
                </ToggleLabel>
              </>
            )}

          </Flex>
        </FeatureGate>

      </Section>
      <Section
        title="View engagement attribute"
        subtitle="These are the SDE attributes that are available in the Conversation Context Service."
      >
        <Flex>
          {
            cardData.map((data, index) => (
              <CardSde
                themeMode={themeMode}
                description={data.cardDescription}
                name={data.cardTitle}
                data-testid={data.cardTitle.replace(/\s/g, '').toLowerCase()}
                onClick={() => handleCardClick(index)}
                isHighlighted={selectedCard === data}
                sdeAttributeTypes={data.hoverData}
                isMarginOff={index + 1 === cardData.length}
                key={data.cardTitle}
              />
            ))
          }
        </Flex>
        {selectedCard && sectionDataArray && sectionDataArray.length && !(
          isLoadingAuthSdeEnabled || isLoadingUnauthSdeEnabled) ? (
            <SdeSectionList
              sectionData={sectionDataArray}
              themeMode={themeMode}
              authSdeEnabled={authSdeEnabled}
              unauthSdeEnabled={unauthSdeEnabled}
              selectedCard={selectedCard}
            />
          ) : null}
      </Section>
    </Box>
  );
}

SdePage.defaultProps = {
  subtitle: undefined,
};

SdePage.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default SdePage;
